import React, { useEffect, useRef, useState } from "react";

const Mon = () => {
  const [mainData, setMainData] = useState([]);
  const [topBox, setTopBox] = useState([[], [], []]);
  const [bottomBox, setBottomBox] = useState([[], [], []]);
  const [serverTime, setServerTime] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 768
  );

  /*
   * 5초 타이머에서 현재 선택된 페이지를
   * 정확하게 참조하기 위한 ref
   */
  const pageRef = useRef(1);

  const serverUrl =
    window.location.hostname === "localhost" ||
    window.location.hostname === "192.168.0.254"
      ? `http://${window.location.hostname}:4000`
      : `${window.location.protocol}//smes.izenimplant.com`;

  ////////////////////////////////////////////////////////////
  // 데이터 조회
  ////////////////////////////////////////////////////////////

  const fetchData = async page => {
    try {
      const res = await fetch(
        `${serverUrl}/api/monitoring?page=${page}&t=${Date.now()}`,
        {
          cache: "no-store"
        }
      );

      if (!res.ok) {
        throw new Error(`조회 실패: ${res.status}`);
      }

      const json = await res.json();

      const main = json[0] || [];
      const sub = json[1] || [];

      const top = [[], [], []];
      const bottom = [[], [], []];

      sub.forEach(r => {
        if (!r.GU) return;

        const [mc, pos] = r.GU.split("-");

        const macNum = Number(mc);
        const position = Number(pos);

        /*
         * 현재 페이지에서 해당 설비가
         * 몇 번째 칸인지 찾음
         */
        const col = main.findIndex(
          machine =>
            Number(machine.MAC_NUM) === macNum
        );

        if (col < 0 || col > 2) {
          return;
        }

        /*
         * 1 ~ 3 : 상단
         * 4 ~ 6 : 하단
         */
        if (position <= 3) {
          top[col].push(r);
        } else {
          bottom[col].push(r);
        }
      });

      setMainData(main);
      setTopBox(top);
      setBottomBox(bottom);

      if (main.length > 0) {
        setServerTime(main[0].DT || "");
      }
    } catch (error) {
      console.error(
        "모니터링 조회 오류:",
        error
      );
    }
  };

  ////////////////////////////////////////////////////////////
  // 최초 조회
  // 현재 선택 페이지 5초마다 재조회
  ////////////////////////////////////////////////////////////

  useEffect(() => {
    /*
     * 최초에는 PAGE 1
     */
    fetchData(1);

    /*
     * 페이지 자체는 변경하지 않음
     *
     * 현재 선택된 페이지 데이터만
     * 5초마다 다시 조회
     */
    const timer = setInterval(() => {
      fetchData(pageRef.current);
    }, 5000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  ////////////////////////////////////////////////////////////
  // 화면 크기 체크
  ////////////////////////////////////////////////////////////

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(
        window.innerWidth <= 768
      );
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);

  ////////////////////////////////////////////////////////////
  // PAGE 버튼 클릭
  ////////////////////////////////////////////////////////////

  const handlePageChange = page => {
    /*
     * 같은 페이지 버튼을 다시 눌러도
     * 즉시 재조회
     */
    if (pageRef.current === page) {
      fetchData(page);
      return;
    }

    pageRef.current = page;

    setCurrentPage(page);

    /*
     * 클릭 즉시 해당 페이지 조회
     */
    fetchData(page);
  };

  ////////////////////////////////////////////////////////////
  // 설비 3칸 유지
  ////////////////////////////////////////////////////////////

  const machineSlots = [
    mainData[0] || null,
    mainData[1] || null,
    mainData[2] || null
  ];

  ////////////////////////////////////////////////////////////
  // 화면
  ////////////////////////////////////////////////////////////

  return (
    <div
      style={{
        ...styles.page,
        padding: isMobile
          ? 12
          : 20
      }}
    >
      {/* HEADER */}

      <div
        style={{
          ...styles.header,
          marginBottom: isMobile
            ? 20
            : 30
        }}
      >
        <div
          style={{
            ...styles.title,
            fontSize: isMobile
              ? 20
              : 26
          }}
        >
          MACHINE MONITORING
        </div>

        <div
          style={
            isMobile
              ? styles.headerRightMobile
              : styles.headerRight
          }
        >
          {/* PAGE */}

          <div style={styles.pageSelector}>
            <span style={styles.pageText}>
              PAGE
            </span>

            <PageButton
              page={1}
              currentPage={currentPage}
              onClick={handlePageChange}
            />

            <PageButton
              page={2}
              currentPage={currentPage}
              onClick={handlePageChange}
            />
          </div>

          {/* 서버시간 */}

          <div
            style={{
              ...styles.clock,
              fontSize: isMobile
                ? 14
                : 18
            }}
          >
            {format(serverTime)}
          </div>
        </div>
      </div>

      {/* PC */}

      {!isMobile && (
        <div style={styles.desktopGrid}>
          {/* 상단 목록 */}

          <StackBox
            list={topBox[0]}
            pos="top"
            isMobile={false}
          />

          <StackBox
            list={topBox[1]}
            pos="top"
            isMobile={false}
          />

          <StackBox
            list={topBox[2]}
            pos="top"
            isMobile={false}
          />

          {/* 설비 */}

          {machineSlots.map(
            (machine, index) =>
              machine ? (
                <Card
                  key={`machine-${machine.MAC_NUM}`}
                  m={machine}
                />
              ) : (
                <EmptyCard
                  key={`empty-${index}`}
                />
              )
          )}

          {/* 하단 목록 */}

          <StackBox
            list={bottomBox[0]}
            pos="bottom"
            isMobile={false}
          />

          <StackBox
            list={bottomBox[1]}
            pos="bottom"
            isMobile={false}
          />

          <StackBox
            list={bottomBox[2]}
            pos="bottom"
            isMobile={false}
          />
        </div>
      )}

      {/* 모바일 */}

      {isMobile && (
        <div style={styles.mobileContainer}>
          {machineSlots.map(
            (machine, index) => (
              <div
                key={`mobile-${index}`}
                style={styles.mobileMachineGroup}
              >
                {/* 상단 */}

                <StackBox
                  list={topBox[index]}
                  pos="top"
                  isMobile={true}
                />

                {/* 설비 */}

                {machine ? (
                  <Card m={machine} />
                ) : (
                  <EmptyCard />
                )}

                {/* 하단 */}

                <StackBox
                  list={bottomBox[index]}
                  pos="bottom"
                  isMobile={true}
                />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

////////////////////////////////////////////////////////////
// PAGE 버튼
////////////////////////////////////////////////////////////

function PageButton({
  page,
  currentPage,
  onClick
}) {
  const active =
    currentPage === page;

  return (
    <button
      type="button"
      onClick={() =>
        onClick(page)
      }
      style={{
        ...styles.pageButton,

        ...(active
          ? styles.pageButtonActive
          : {})
      }}
    >
      {page}
    </button>
  );
}

////////////////////////////////////////////////////////////
// StackBox
////////////////////////////////////////////////////////////

function StackBox({
  list,
  pos,
  isMobile
}) {
  const rows = list.slice(0, 3);

  let borderColor = "#ff2d2d";

  if (pos === "top") {
    borderColor = "#7c4dff";
  }

  if (pos === "bottom") {
    borderColor = "#40c4ff";
  }

  return (
    <div
      style={{
        ...styles.stack,

        border:
          `2px solid ${borderColor}`
      }}
    >
      {rows.map((d, i) => (
        <div
          key={`${d.GU}-${i}`}
          style={
            isMobile
              ? styles.stackRowMobile
              : styles.stackRow
          }
        >
          {isMobile ? (
            <>
              <div style={styles.mobileLine}>
                <span style={styles.cell}>
                  {d.PPI_PROJECTNO || "-"}
                </span>

                <span
                  style={{
                    ...styles.cell,
                    textAlign: "right"
                  }}
                >
                  {d.PPI_DENTALNUM || "-"}
                </span>
              </div>

              <div style={styles.mobileLine}>
                <span style={styles.cell}>
                  {d.BCU_NAME || "-"}
                </span>

                <span
                  style={{
                    ...styles.cell,
                    ...styles.patient
                  }}
                >
                  {d.PPI_PATIENTNAME || "-"}
                </span>
              </div>
            </>
          ) : (
            <>
              <span style={styles.cell}>
                {d.PPI_PROJECTNO || "-"}
              </span>

              <span style={styles.cell}>
                {d.PPI_DENTALNUM || "-"}
              </span>

              <span style={styles.cell}>
                {d.BCU_NAME || "-"}
              </span>

              <span
                style={{
                  ...styles.cell,
                  ...styles.patient
                }}
              >
                {d.PPI_PATIENTNAME || "-"}
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

////////////////////////////////////////////////////////////
// Card
////////////////////////////////////////////////////////////

function Card({ m }) {
  const color =
    getColor(m.STATE);

  return (
    <div
      style={{
        ...styles.card,
        borderColor: color
      }}
    >
      {/* 상태 */}

      <div
        style={{
          ...styles.state,
          background: color
        }}
      >
        {m.STATE}
      </div>

      {/* NC */}

      <div style={styles.mc}>
        NC{m.MAC_NUM}
      </div>

      <Row
        label="설비"
        value={m.BMA_NAME || "-"}
      />

      <Row
        label="O NUMBER"
        value={
          m.PROGRAM_NUMBER || "-"
        }
      />

      <Row
        label="TOOL NUMBER"
        value={
          m.TOOL_NUMBER || "-"
        }
      />

      <Row
        label="치식 / 거래처 / 환자"
        value={`${
          m.PPI_DENTALNUM || "-"
        } / ${
          m.BCU_NAME || "-"
        } / ${
          m.PPI_PATIENTNAME || "-"
        }`}
      />

      <Row
        label="COUNT"
        value={Number(
          m.PART_COUNT || 0
        ).toLocaleString()}
      />

      <Row
        label="TIME"
        value={
          format(m.G_DATETIME)
        }
      />
    </div>
  );
}

////////////////////////////////////////////////////////////
// 빈 설비
////////////////////////////////////////////////////////////

function EmptyCard() {
  return (
    <div style={styles.emptyCard}>
      <div style={styles.emptyText}>
        NO MACHINE
      </div>
    </div>
  );
}

////////////////////////////////////////////////////////////
// Row
////////////////////////////////////////////////////////////

function Row({
  label,
  value
}) {
  return (
    <div style={styles.row2}>
      <div style={styles.label}>
        {label}
      </div>

      <div style={styles.value}>
        {value}
      </div>
    </div>
  );
}

////////////////////////////////////////////////////////////
// 날짜
////////////////////////////////////////////////////////////

function format(t) {
  if (!t) {
    return "";
  }

  return String(t)
    .replace("T", " ")
    .replace("Z", "")
    .split(".")[0];
}

////////////////////////////////////////////////////////////
// 상태 색상
////////////////////////////////////////////////////////////

function getColor(state) {
  switch (state) {
    case "EMERGENCY":
      return "#ff1744";

    case "ALARM":
      return "#ff5252";

    case "EDIT":
      return "#ffd600";

    case "RUNNING":
      return "#00e676";

    case "IDLE":
      return "#40c4ff";

    case "SETUP":
      return "#ff9100";

    case "POWER_ON":
      return "#9e9e9e";

    default:
      return "#555";
  }
}

////////////////////////////////////////////////////////////
// STYLE
////////////////////////////////////////////////////////////

const styles = {
  page: {
    background: "#0b0f14",

    minHeight: "100vh",

    boxSizing: "border-box",

    fontFamily: "Pretendard",

    color: "#fff"
  },

  ////////////////////////////////////////////////////////////
  // Header
  ////////////////////////////////////////////////////////////

  header: {
    position: "relative",

    minHeight: 45
  },

  title: {
    textAlign: "center",

    fontWeight: 800
  },

  headerRight: {
    position: "absolute",

    right: 0,

    top: 0,

    display: "flex",

    alignItems: "center",

    gap: 20
  },

  headerRightMobile: {
    marginTop: 12,

    display: "flex",

    alignItems: "center",

    justifyContent: "flex-end",

    gap: 12
  },

  ////////////////////////////////////////////////////////////
  // PAGE
  ////////////////////////////////////////////////////////////

  pageSelector: {
    display: "flex",

    alignItems: "center",

    gap: 6
  },

  pageText: {
    marginRight: 2,

    fontSize: 16,

    fontWeight: 700,

    color: "#40c4ff"
  },

  pageButton: {
    width: 32,

    height: 30,

    padding: 0,

    border:
      "1px solid #35404d",

    borderRadius: 6,

    background: "#121821",

    color: "#9aa3ad",

    fontSize: 14,

    fontWeight: 800,

    cursor: "pointer"
  },

  pageButtonActive: {
    background: "#40c4ff",

    borderColor: "#40c4ff",

    color: "#000"
  },

  clock: {
    fontWeight: 600,

    whiteSpace: "nowrap"
  },

  ////////////////////////////////////////////////////////////
  // PC Grid
  ////////////////////////////////////////////////////////////

  desktopGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(3, minmax(0, 1fr))",

    gap: 20,

    marginBottom: 20
  },

  ////////////////////////////////////////////////////////////
  // 모바일
  ////////////////////////////////////////////////////////////

  mobileContainer: {
    display: "flex",

    flexDirection: "column",

    gap: 20
  },

  mobileMachineGroup: {
    display: "flex",

    flexDirection: "column",

    gap: 20
  },

  ////////////////////////////////////////////////////////////
  // Stack
  ////////////////////////////////////////////////////////////

  stack: {
    minHeight: 150,

    borderRadius: 12,

    padding: 16,

    boxSizing: "border-box",

    display: "flex",

    flexDirection: "column",

    justifyContent:
      "flex-start",

    gap: 14,

    overflow: "hidden"
  },

  stackRow: {
    display: "grid",

    gridTemplateColumns:
      "repeat(4, minmax(0, 1fr))",

    alignItems: "center",

    gap: 12,

    fontSize: 18,

    borderBottom:
      "1px solid #333",

    paddingBottom: 6
  },

  stackRowMobile: {
    display: "flex",

    flexDirection: "column",

    gap: 6,

    fontSize: 18,

    borderBottom:
      "1px solid #333",

    paddingBottom: 6
  },

  mobileLine: {
    display: "grid",

    gridTemplateColumns:
      "minmax(0, 1fr) minmax(0, 1fr)",

    gap: 12
  },

  cell: {
    minWidth: 0,

    whiteSpace: "nowrap",

    overflow: "hidden",

    textOverflow: "ellipsis"
  },

  patient: {
    color: "#8be9fd",

    textAlign: "right"
  },

  ////////////////////////////////////////////////////////////
  // Card
  ////////////////////////////////////////////////////////////

  card: {
    position: "relative",

    background: "#121821",

    borderRadius: 16,

    padding: 24,

    boxSizing: "border-box",

    border: "4px solid",

    minHeight: 330
  },

  emptyCard: {
    background: "#121821",

    borderRadius: 16,

    padding: 24,

    boxSizing: "border-box",

    border:
      "4px solid #252c35",

    minHeight: 330,

    display: "flex",

    justifyContent:
      "center",

    alignItems: "center"
  },

  emptyText: {
    color: "#555",

    fontSize: 22,

    fontWeight: 800
  },

  state: {
    position: "absolute",

    top: 15,

    right: 15,

    padding: "6px 14px",

    borderRadius: 8,

    fontWeight: 700,

    fontSize: 12,

    color: "#000"
  },

  mc: {
    fontSize: 26,

    fontWeight: 800,

    marginBottom: 20
  },

  row2: {
    marginTop: 12
  },

  label: {
    fontSize: 12,

    color: "#9aa3ad"
  },

  value: {
    fontSize: 18,

    fontWeight: 700,

    wordBreak: "break-word"
  }
};

export default Mon;